import React from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  Popover,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommuteIcon from '@mui/icons-material/Commute';
import InfoIcon from '@mui/icons-material/Info';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { ACTIVITY_TYPES_WITH_DESCRIPTIONS, BUDGET_CATEGORIES, TRANSPORTATION_TYPES } from '../../constants/travel';
import { initializeTripParameters, calculateNumberOfDays } from '../../utils/travel';
import { TripParameters } from '../../types/chat';
import { usePlanner } from '../../contexts/PlannerContext';
import { loadGoogleMapsScript } from '../../services/googleMapsApi';
import { createLogger } from '../../utils/logger';
import '../../types/google-maps.d.ts';
import { textScale } from '../../theme/standards';

const log = createLogger('PlannerInputPanel');

const POPULAR_DESTINATIONS = [
  'London, United Kingdom',
  'Paris, France',
  'New York, United States',
  'Tokyo, Japan',
  'Rome, Italy',
  'Barcelona, Spain',
  'Dubai, United Arab Emirates',
  'Sydney, Australia',
  'Rio de Janeiro, Brazil',
  'Vienna, Austria',
  'Amsterdam, Netherlands',
  'Istanbul, Turkey',
  'Bangkok, Thailand',
  'Singapore, Singapore',
  'San Francisco, United States',
  'Berlin, Germany',
  'Prague, Czech Republic',
  'Lisbon, Portugal',
  'Bali, Indonesia',
  'Santorini, Greece',
];

interface PlannerInputPanelProps {
  tripParameters?: TripParameters;
  onTripParametersChange?: (params: TripParameters) => void;
}

interface PlaceSuggestion {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text?: string;
  };
}

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

export const PlannerInputPanel: React.FC<PlannerInputPanelProps> = ({
  tripParameters: externalTripParameters,
  onTripParametersChange
}) => {
  const { generatePlan, loading, loadingPhase, error } = usePlanner();
  const [internalTripParameters, setInternalTripParameters] = React.useState<TripParameters>(initializeTripParameters());
  const internalTripParametersRef = React.useRef(internalTripParameters);
  const externalTripParametersRef = React.useRef(externalTripParameters);

  React.useEffect(() => {
    internalTripParametersRef.current = internalTripParameters;
  }, [internalTripParameters]);
  React.useEffect(() => {
    externalTripParametersRef.current = externalTripParameters;
  }, [externalTripParameters]);
  const autocompleteContainerRef = React.useRef<HTMLDivElement | null>(null);
  const autocompleteServiceRef = React.useRef<any>(null);
  const sessionTokenRef = React.useRef<any>(null);
  const debounceTimeoutRef = React.useRef<number | null>(null);
  const predictionRequestIdRef = React.useRef(0);
  const [suggestions, setSuggestions] = React.useState<PlaceSuggestion[]>([]);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = React.useState(false);
  const [isPlacesReady, setIsPlacesReady] = React.useState(false);
  const [expandedSections, setExpandedSections] = React.useState<{
    interests: boolean;
    transportation: boolean;
    extraContext: boolean;
  }>({
    interests: false,
    transportation: false,
    extraContext: false,
  });
  const [showDaysLimitWarning, setShowDaysLimitWarning] = React.useState(false);

  // Calendar State
  const [calendarAnchorEl, setCalendarAnchorEl] = React.useState<HTMLDivElement | null>(null);

  const tripParameters = externalTripParameters ?? internalTripParameters;
  const setTripParameters = React.useCallback((params: TripParameters | ((prev: TripParameters) => TripParameters)) => {
    if (onTripParametersChange) {
      const current = externalTripParametersRef.current ?? internalTripParametersRef.current;
      const newParams = typeof params === 'function' ? params(current) : params;
      onTripParametersChange(newParams);
    } else {
      setInternalTripParameters((prev) => (typeof params === 'function' ? params(prev) : params));
    }
  }, [onTripParametersChange]);

  React.useEffect(() => {
    let isMounted = true;

    const initializePlacesService = async () => {
      try {
        await loadGoogleMapsScript();

        if (!isMounted || !window.google?.maps?.places) {
          return;
        }

        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setIsPlacesReady(true);
      } catch (error) {
        log.error('Failed to initialize Google Places service', error);
      }
    };

    initializePlacesService();

    return () => {
      isMounted = false;
      if (debounceTimeoutRef.current !== null) {
        window.clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, []);

  const schedulePredictionFetch = React.useCallback((inputValue: string) => {
    if (!isPlacesReady) {
      return;
    }

    if (debounceTimeoutRef.current !== null) {
      window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (!inputValue.trim()) {
      predictionRequestIdRef.current += 1;
      setSuggestions([]);
      setIsSuggestionsVisible(false);
      return;
    }

    debounceTimeoutRef.current = window.setTimeout(() => {
      if (!autocompleteServiceRef.current) {
        return;
      }

      predictionRequestIdRef.current += 1;
      const currentRequestId = predictionRequestIdRef.current;

      const request = {
        input: inputValue,
        types: ['(cities)'],
        sessionToken: sessionTokenRef.current ?? undefined,
      };

      autocompleteServiceRef.current.getPlacePredictions(request, (predictions: PlaceSuggestion[] | null, status: string) => {
        if (currentRequestId !== predictionRequestIdRef.current) {
          return;
        }

        if (status !== 'OK' || !predictions?.length) {
          setSuggestions([]);
          setIsSuggestionsVisible(false);
          return;
        }

        setSuggestions(predictions);
        setIsSuggestionsVisible(true);
      });
    }, 180);
  }, [isPlacesReady]);

  React.useEffect(() => {
    if (!isSuggestionsVisible) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteContainerRef.current && !autocompleteContainerRef.current.contains(event.target as Node)) {
        setIsSuggestionsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSuggestionsVisible]);

  const handleDestinationChange = (value: string) => {
    setTripParameters((prev) => ({ ...prev, destination: value }));

    if (!value.trim()) {
      setSuggestions([]);
      setIsSuggestionsVisible(false);
      return;
    }

    if (isPlacesReady) {
      schedulePredictionFetch(value);
    }
  };

  const handleSuggestionSelect = (prediction: PlaceSuggestion) => {
    const mainText = prediction.structured_formatting?.main_text || prediction.description;
    const secondaryText = prediction.structured_formatting?.secondary_text || '';
    const formattedDestination = secondaryText ? `${mainText}, ${secondaryText}` : mainText;

    if (!formattedDestination) {
      return;
    }

    setTripParameters((prev) => ({
      ...prev,
      destination: formattedDestination,
    }));

    setSuggestions([]);
    setIsSuggestionsVisible(false);

    if (window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  };

  const handleGenerate = () => {
    generatePlan(tripParameters, { confirmCreation: true });
  };

  const handleRandomizeDestination = () => {
    const randomIndex = Math.floor(Math.random() * POPULAR_DESTINATIONS.length);
    const randomDestination = POPULAR_DESTINATIONS[randomIndex];
    setTripParameters((prev) => ({
      ...prev,
      destination: randomDestination,
    }));
  };

  // Calendar Logic
  const handleCalendarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const handleDateChange = (value: Value) => {
    if (Array.isArray(value) && value[0] && value[1]) {
      const [start, end] = value;
      
      // Convert to local YYYY-MM-DD strings to avoid timezone shifts
      // We use local time because the calendar component returns dates in local time (mostly) 
      // and we want the "selected date" regardless of time.
      const toLocalISO = (date: Date) => {
        const offset = date.getTimezoneOffset();
        const adjusted = new Date(date.getTime() - (offset * 60 * 1000));
        return adjusted.toISOString().split('T')[0];
      };

      const startStr = toLocalISO(start);
      const endStr = toLocalISO(end);

      const days = calculateNumberOfDays(startStr, endStr);
      
      if (days > 14) {
        setShowDaysLimitWarning(true);
        // Clamp to 14 days
        const clampedEnd = new Date(start);
        clampedEnd.setDate(clampedEnd.getDate() + 13);
        const clampedEndStr = toLocalISO(clampedEnd);
        
        setTripParameters(prev => ({
          ...prev,
          startDate: startStr,
          endDate: clampedEndStr,
          numberOfDays: 14
        }));
      } else {
        setShowDaysLimitWarning(false);
        setTripParameters(prev => ({
          ...prev,
          startDate: startStr,
          endDate: endStr,
          numberOfDays: days
        }));
      }
      handleCalendarClose();
    } else if (value instanceof Date) {
        // Just start date selected (in some modes), wait for range?
        // With selectRange={true}, it might pass [Date] or [Date, Date]
        // We only close on full range
    }
  };

  const formattedDateRange = React.useMemo(() => {
    if (!tripParameters.startDate || !tripParameters.endDate) return '';
    try {
      const start = new Date(tripParameters.startDate);
      const end = new Date(tripParameters.endDate);
      
      const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
      const startDay = start.getDate();
      const endDay = end.getDate();
      const year = end.getFullYear();

      if (tripParameters.startDate === tripParameters.endDate) {
        return `${startMonth} ${startDay}, ${year}`;
      }
      
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay} – ${endDay}, ${year}`;
      }
      
      return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
    } catch (e) {
      return '';
    }
  }, [tripParameters.startDate, tripParameters.endDate]);

  const calendarOpen = Boolean(calendarAnchorEl);
  const calendarId = calendarOpen ? 'trip-dates-popover' : undefined;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        px: { xs: 2, md: 2.5 },
        pt: { xs: 2, md: 2.5 },
        pb: { xs: 0.5, md: 0.75 },
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
      })}
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Box
                component="img"
                src="/favicon.svg"
                alt="TripGen AI Logo"
                sx={{ 
                  width: { xs: '1.25rem', sm: '1.75rem' },
                  height: { xs: '1.25rem', sm: '1.75rem' },
                  flexShrink: 0,
                  display: 'block'
                }}
              />
              <Typography
                variant="h3"
                sx={(theme) => ({
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0,
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                })}
              >
                Build your next trip
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Give the essentials—location, timing, and interests—and TripGen does the rest.
            </Typography>
          </Box>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: -1.5 }}>
          <Box ref={autocompleteContainerRef} sx={{ position: 'relative', width: '100%' }}>
            <TextField
              label="Destination"
              fullWidth
              value={tripParameters.destination}
              onChange={(e) => handleDestinationChange(e.target.value)}
              placeholder="e.g. Kyoto, Japan"
              autoComplete="off"
              onFocus={() => {
                if (tripParameters.destination && isPlacesReady) {
                  schedulePredictionFetch(tripParameters.destination);
                }
                if (suggestions.length > 0) {
                  setIsSuggestionsVisible(true);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setIsSuggestionsVisible(false);
                  setSuggestions([]);
                }
                if (event.key === 'Enter' && suggestions.length > 0) {
                  event.preventDefault();
                  handleSuggestionSelect(suggestions[0]);
                }
              }}
              InputProps={{
                endAdornment: (
                  <Tooltip title="Pick a random destination">
                    <IconButton
                      onClick={handleRandomizeDestination}
                      edge="end"
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)
                        }
                      }}
                    >
                      <ShuffleIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
            {isSuggestionsVisible && suggestions.length > 0 && (
              <Paper
                elevation={6}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 1,
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: (theme) => theme.shadows[10],
                  zIndex: (theme) => theme.zIndex.modal,
                  maxHeight: 320,
                  overflowY: 'auto',
                }}
              >
                {suggestions.map((suggestion) => {
                  const { place_id: placeId, structured_formatting: formatting } = suggestion;
                  const mainText = formatting?.main_text || suggestion.description;
                  const secondaryText = formatting?.secondary_text;

                  return (
                    <Box
                      key={placeId}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSuggestionSelect(suggestion)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.action.hover,
                        },
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {mainText}
                      </Typography>
                      {secondaryText && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                          {secondaryText}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Box>
        </Stack>

        <Box sx={{ position: 'relative', width: '100%' }}>
          <TextField
            label="Trip dates"
            fullWidth
            value={formattedDateRange}
            onClick={handleCalendarClick}
            placeholder="Select your trip dates"
            InputProps={{
              readOnly: true,
              startAdornment: (
                 <CalendarTodayIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />
              )
            }}
            sx={{
              cursor: 'pointer',
              '& .MuiInputBase-root': {
                cursor: 'pointer',
              },
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              }
            }}
          />
          <Popover
            id={calendarId}
            open={calendarOpen}
            anchorEl={calendarAnchorEl}
            onClose={handleCalendarClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 4,
                boxShadow: (theme) => theme.shadows[4],
                overflow: 'hidden',
              }
            }}
          >
             <Box sx={{
                p: 2,
                '& .react-calendar': {
                  width: '320px',
                  maxWidth: '100%',
                  backgroundColor: 'background.paper',
                  fontFamily: 'inherit',
                  border: 'none',
                },
                '& .react-calendar__navigation': {
                  marginBottom: 1,
                  height: '44px',
                },
                '& .react-calendar__navigation button': {
                  minWidth: 40,
                  background: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  borderRadius: 1,
                },
                '& .react-calendar__month-view__weekdays': {
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  marginBottom: 1,
                  textDecoration: 'none',
                },
                '& .react-calendar__month-view__days__day--weekend': {
                   color: 'text.primary',
                },
                '& .react-calendar__tile': {
                  padding: '10px 6px',
                  fontSize: '0.9rem',
                  borderRadius: 2,
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&:enabled:focus': {
                    backgroundColor: 'action.hover',
                  },
                },
                '& .react-calendar__tile--now': {
                  backgroundColor: 'action.selected',
                  color: 'primary.main',
                },
                '& .react-calendar__tile--active': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '& .react-calendar__tile--range': {
                    borderRadius: 0,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                    color: 'text.primary',
                },
                '& .react-calendar__tile--rangeStart': {
                    borderTopLeftRadius: '50%',
                    borderBottomLeftRadius: '50%',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                },
                '& .react-calendar__tile--rangeEnd': {
                    borderTopRightRadius: '50%',
                    borderBottomRightRadius: '50%',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                },
                '& abbr[title]': {
                    textDecoration: 'none',
                }
              }}>
              <Calendar
                selectRange={true}
                onChange={handleDateChange}
                value={
                  tripParameters.startDate && tripParameters.endDate
                   ? [new Date(tripParameters.startDate), new Date(tripParameters.endDate)]
                   : null
                }
                minDate={new Date()}
              />
            </Box>
          </Popover>
        </Box>
        
        <Stack direction="row" spacing={1.5}>
          {showDaysLimitWarning && (
            <Box sx={{ width: '100%', mt: 1.5 }}>
              <Alert 
                severity="info" 
                icon={<InfoIcon />}
                sx={{ 
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem',
                  }
                }}
              >
                For the planner, the limit for the trip is 2 weeks for a single destination.
              </Alert>
            </Box>
          )}
        </Stack>

        <Box>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <AttachMoneyIcon /> Budget
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {BUDGET_CATEGORIES.map((category) => (
              <Chip
                key={category}
                label={category}
                clickable
                onClick={() => setTripParameters((prev) => ({ ...prev, budget: category }))}
                sx={(theme) => ({
                  px: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  transition: 'all 0.2s ease',
                  fontSize: '0.875rem',
                  ...(tripParameters.budget === category
                    ? {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.28)}`,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }
                    : {
                        backgroundColor: alpha(theme.palette.text.primary, 0.04),
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.text.primary, 0.08),
                        },
                      }),
                })}
              />
            ))}
          </Stack>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <FavoriteIcon /> Interests
            </Typography>
            {ACTIVITY_TYPES_WITH_DESCRIPTIONS.length > 4 && (
              <Button
                size="small"
                onClick={() => setExpandedSections(prev => ({ ...prev, interests: !prev.interests }))}
                endIcon={expandedSections.interests ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  minWidth: 'auto',
                  px: 1,
                }}
              >
                {expandedSections.interests ? 'Show less' : 'Show more'}
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(expandedSections.interests 
              ? ACTIVITY_TYPES_WITH_DESCRIPTIONS 
              : ACTIVITY_TYPES_WITH_DESCRIPTIONS.slice(0, 4)
            ).map((activity) => {
              const isSelected = tripParameters.preferredActivityTypes.includes(activity.value);
              return (
                <Chip
                  key={activity.value}
                  label={activity.label}
                  clickable
                  onClick={() =>
                    setTripParameters((prev) => {
                      const exists = prev.preferredActivityTypes.includes(activity.value);
                      return {
                        ...prev,
                        preferredActivityTypes: exists
                          ? prev.preferredActivityTypes.filter((item) => item !== activity.value)
                          : [...prev.preferredActivityTypes, activity.value],
                      };
                    })
                  }
                  sx={(theme) => ({
                    px: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    transition: 'all 0.2s ease',
                    fontSize: '0.875rem',
                    ...(isSelected
                      ? {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.28)}`,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }
                      : {
                          backgroundColor: alpha(theme.palette.text.primary, 0.04),
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.text.primary, 0.08),
                          },
                        }),
                  })}
                />
              );
            })}
          </Stack>
        </Box>

        <Box>
          <Button
            fullWidth
            onClick={() => {
              const shouldExpand = !(expandedSections.transportation || expandedSections.extraContext);
              setExpandedSections({
                interests: expandedSections.interests,
                transportation: shouldExpand,
                extraContext: shouldExpand,
              });
            }}
            endIcon={(expandedSections.transportation || expandedSections.extraContext) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={(theme) => ({
              justifyContent: 'flex-start',
              textTransform: 'none',
              color: 'text.secondary',
              px: 1,
              py: 0.5,
              mb: (expandedSections.transportation || expandedSections.extraContext) ? 1 : 0,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                color: 'text.primary',
              },
            })}
          >
            {(expandedSections.transportation || expandedSections.extraContext) ? 'Hide additional options' : 'Show additional options'}
          </Button>
          
          <Collapse in={expandedSections.transportation || expandedSections.extraContext}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <CommuteIcon /> Getting around
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {TRANSPORTATION_TYPES.map((mode) => {
                    const label = mode.replace(/_/g, ' ');
                    const isSelected = tripParameters.preferredTransportationTypes.includes(mode);
                    return (
                      <Chip
                        key={mode}
                        label={label}
                        clickable
                        onClick={() =>
                          setTripParameters((prev) => {
                            const exists = prev.preferredTransportationTypes.includes(mode);
                            return {
                              ...prev,
                              preferredTransportationTypes: exists
                                ? prev.preferredTransportationTypes.filter((item) => item !== mode)
                                : [...prev.preferredTransportationTypes, mode],
                            };
                          })
                        }
                        sx={(theme) => ({
                          px: 1.5,
                          borderRadius: 2,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          transition: 'all 0.2s ease',
                          fontSize: '0.875rem',
                          ...(isSelected
                            ? {
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.28)}`,
                                '&:hover': {
                                  backgroundColor: theme.palette.primary.dark,
                                },
                              }
                            : {
                                backgroundColor: alpha(theme.palette.text.primary, 0.04),
                                border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.text.primary, 0.08),
                                },
                              }),
                        })}
                      />
                    );
                  })}
                </Stack>
              </Box>

              <Box sx={{
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.02),
                border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                borderRadius: 3,
                p: 1.5,
              }}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <InfoIcon /> Extra context
                </Typography>
                <TextField
                  multiline
                  minRows={2}
                  value={tripParameters.additionalInfo}
                  onChange={(e) => setTripParameters((prev) => ({ ...prev, additionalInfo: e.target.value }))}
                  placeholder="Add special requests, must-see attractions, travel companions, mobility needs, dietary notes, etc."
                  fullWidth
                />
              </Box>
            </Stack>
          </Collapse>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5, pb: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleGenerate}
            disabled={loading || !tripParameters.destination.trim()}
            sx={(theme) => ({
              px: { xs: 4, md: 6 },
              py: 1.5,
              fontSize: textScale.body.fontSize,
              fontWeight: 700,
              borderRadius: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`,
              boxShadow: 'none',
              minWidth: { xs: '80%', sm: '60%', md: '50%' },
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              },
            })}
          >
            {loading ? 'Creating your plan…' : 'Generate my travel plan'}
          </Button>
        </Box>

        {loading && (
          <Box>
            <LinearProgress />
            {loadingPhase && loadingPhase !== 'activities_complete' && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {loadingPhase === 'activities_coordination' && 'Coordinating activities…'}
                {loadingPhase === 'activities_preliminary' && 'Drafting activity lineup…'}
                {loadingPhase === 'restaurants_preliminary' && 'Finding dining recommendations…'}
                {!['activities_coordination', 'activities_preliminary', 'restaurants_preliminary'].includes(loadingPhase) && loadingPhase}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
